import React, { useState, useEffect, useRef } from 'react';
import grapesjs from 'grapesjs';
import 'grapesjs/dist/css/grapes.min.css';
import websitePlugin from 'grapesjs-preset-webpage';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Paintbrush, Save, X } from 'lucide-react';
import PremiumPopup from './PremiumPopup';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function VisualPageBuilder({ pageId, isAdmin, onSaved, defaultContent }) {
  const [isOpen, setIsOpen] = useState(false);
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);
  const editorRef = useRef(null);
  const [popup, setPopup] = useState({ isOpen: false, type: 'info', message: '' });
  const [confirmClose, setConfirmClose] = useState(false);

  useEffect(() => {
    if (isOpen && editorRef.current && !editor) {
      const editorInstance = grapesjs.init({
        container: editorRef.current,
        height: 'calc(100vh - 140px)',
        width: '100%',
        plugins: [websitePlugin],
        storageManager: false,
        fromElement: false,
        canvas: {
          styles: [
            'https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css'
          ]
        },
        // Enable all editing features
        allowScripts: 1,
        showOffsets: 1,
        noticeOnUnload: 0,
        
        blockManager: {
          appendTo: '#blocks-container',
          blocks: [
            {
              id: 'section',
              label: '<div style="text-align:center"><i class="fa fa-square-o"></i><br/>Ενότητα</div>',
              content: `<section class="bg-white/5 rounded-xl p-8 border border-white/10 text-white" style="margin-bottom: 1rem;">
                <h2 class="text-3xl font-bold mb-4">Νέα Ενότητα</h2>
                <p class="text-gray-300">Προσθέστε περιεχόμενο εδώ...</p>
              </section>`,
              category: 'Βασικά'
            },
            {
              id: 'heading',
              label: '<div style="text-align:center"><i class="fa fa-header"></i><br/>Τίτλος</div>',
              content: '<h2 class="text-4xl font-bold text-white mb-4" style="margin-bottom: 1rem;">Τίτλος</h2>',
              category: 'Κείμενο'
            },
            {
              id: 'text',
              label: '<div style="text-align:center"><i class="fa fa-align-left"></i><br/>Παράγραφος</div>',
              content: '<p class="text-gray-300 mb-4" style="margin-bottom: 1rem;">Γράψτε το κείμενό σας εδώ...</p>',
              category: 'Κείμενο'
            },
            {
              id: 'button',
              label: '<div style="text-align:center"><i class="fa fa-hand-pointer-o"></i><br/>Κουμπί</div>',
              content: '<button class="bg-white text-black hover:bg-gray-200 px-6 py-3 rounded-lg font-semibold" style="margin: 0.5rem;">Κουμπί</button>',
              category: 'Βασικά'
            },
            {
              id: 'image',
              label: '<div style="text-align:center"><i class="fa fa-image"></i><br/>Εικόνα</div>',
              content: { 
                type: 'image',
                style: { 'max-width': '100%', 'margin-bottom': '1rem' }
              },
              category: 'Μέσα'
            },
            {
              id: 'two-columns',
              label: '<div style="text-align:center"><i class="fa fa-columns"></i><br/>2 Στήλες</div>',
              content: `<div class="grid grid-cols-2 gap-6" style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 1.5rem; margin-bottom: 1rem;">
                <div class="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h3 class="text-xl font-semibold text-white mb-2">Στήλη 1</h3>
                  <p class="text-gray-300">Περιεχόμενο...</p>
                </div>
                <div class="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h3 class="text-xl font-semibold text-white mb-2">Στήλη 2</h3>
                  <p class="text-gray-300">Περιεχόμενο...</p>
                </div>
              </div>`,
              category: 'Layout'
            },
            {
              id: 'card',
              label: '<div style="text-align:center"><i class="fa fa-id-card-o"></i><br/>Κάρτα</div>',
              content: `<div class="bg-white/5 rounded-xl p-6 border border-white/10" style="margin-bottom: 1rem;">
                <h3 class="text-2xl font-semibold text-white mb-3">Τίτλος Κάρτας</h3>
                <p class="text-gray-300">Περιεχόμενο κάρτας...</p>
              </div>`,
              category: 'Βασικά'
            },
            {
              id: 'hero',
              label: '<div style="text-align:center"><i class="fa fa-star"></i><br/>Hero</div>',
              content: `<div class="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl p-12 text-center border border-white/10" style="margin-bottom: 1rem;">
                <h1 class="text-5xl font-bold text-white mb-4">Καλώς ήρθες</h1>
                <p class="text-xl text-gray-300 mb-8">Η περιγραφή σου εδώ</p>
                <button class="bg-white text-black hover:bg-gray-200 px-8 py-3 rounded-lg font-semibold text-lg">Ξεκίνα Τώρα</button>
              </div>`,
              category: 'Layout'
            },
            {
              id: 'spacer',
              label: '<div style="text-align:center"><i class="fa fa-arrows-v"></i><br/>Διάστημα</div>',
              content: '<div style="height: 2rem; margin: 1rem 0;"></div>',
              category: 'Layout'
            },
            {
              id: 'divider',
              label: '<div style="text-align:center"><i class="fa fa-minus"></i><br/>Γραμμή</div>',
              content: '<hr style="border: 1px solid rgba(255,255,255,0.1); margin: 2rem 0;">',
              category: 'Layout'
            }
          ]
        },
        
        styleManager: {
          appendTo: '#styles-container',
          sectors: [
            {
              name: 'Γενικά',
              open: true,
              properties: [
                'display',
                'position',
                'top',
                'right',
                'bottom',
                'left'
              ]
            },
            {
              name: 'Διαστάσεις',
              open: false,
              properties: [
                'width',
                'height',
                'max-width',
                'min-height',
                'margin',
                'padding'
              ]
            },
            {
              name: 'Κείμενο',
              open: false,
              properties: [
                'font-family',
                'font-size',
                'font-weight',
                'letter-spacing',
                'color',
                'line-height',
                'text-align'
              ]
            },
            {
              name: 'Χρώματα & Borders',
              open: false,
              properties: [
                'color',
                'background-color',
                'border-radius',
                'border',
                'border-color'
              ]
            }
          ]
        },
        
        layerManager: {
          appendTo: '#layers-container'
        },
        
        traitManager: {
          appendTo: '#traits-container'
        },
        
        selectorManager: {
          appendTo: '#selectors-container'
        },
        
        panels: {
          defaults: [
            {
              id: 'basic-actions',
              el: '.panel__basic-actions',
              buttons: [
                {
                  id: 'visibility',
                  active: true,
                  className: 'btn-toggle-borders',
                  label: '<i class="fa fa-clone"></i>',
                  command: 'sw-visibility'
                },
                {
                  id: 'fullscreen',
                  className: 'btn-fullscreen',
                  label: '<i class="fa fa-arrows-alt"></i>',
                  command: 'fullscreen',
                  context: 'fullscreen'
                },
                {
                  id: 'undo',
                  className: 'btn-undo',
                  label: '<i class="fa fa-undo"></i>',
                  command: 'undo'
                },
                {
                  id: 'redo',
                  className: 'btn-redo',
                  label: '<i class="fa fa-repeat"></i>',
                  command: 'redo'
                },
                {
                  id: 'clean-all',
                  className: 'btn-clear',
                  label: '<i class="fa fa-trash"></i>',
                  command: 'canvas-clear'
                }
              ]
            }
          ]
        },
        
        deviceManager: {
          devices: [
            {
              name: 'Desktop',
              width: ''
            },
            {
              name: 'Tablet',
              width: '768px',
              widthMedia: '992px'
            },
            {
              name: 'Mobile',
              width: '320px',
              widthMedia: '480px'
            }
          ]
        }
      });

      setEditor(editorInstance);

      // Load existing content
      loadPageContent(editorInstance);
      
      console.log('✅ Visual Builder initialized with full editing capabilities');
    }

    return () => {
      if (editor) {
        editor.destroy();
        setEditor(null);
      }
    };
  }, [isOpen, pageId]);

  const loadPageContent = async (editorInstance) => {
    try {
      const response = await axios.get(`${API}/pages/${pageId}`);
      if (response.data.html) {
        editorInstance.setComponents(response.data.html);
        if (response.data.css) {
          editorInstance.setStyle(response.data.css);
        }
      } else if (defaultContent) {
        // Load default page content if nothing is saved yet
        editorInstance.setComponents(defaultContent);
      }
    } catch (error) {
      console.error('Failed to load page content:', error);
      if (defaultContent) {
        editorInstance.setComponents(defaultContent);
      }
    }
  };

  const handleSave = async () => {
    if (!editor) return;

    setSaving(true);
    try {
      const html = editor.getHtml();
      const css = editor.getCss();

      await axios.post(
        `${API}/pages/${pageId}`,
        { html, css },
        { withCredentials: true }
      );

      setPopup({ isOpen: true, type: 'success', message: 'Η σελίδα αποθηκεύτηκε επιτυχώς! Οι αλλαγές είναι live για όλους!' });
      if (onSaved) onSaved();
    } catch (error) {
      console.error('Failed to save:', error);
      setPopup({ isOpen: true, type: 'error', message: 'Αποτυχία αποθήκευσης. Δοκίμασε ξανά.' });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (editor && editor.getDirtyCount() > 0) {
      setConfirmClose(true);
    } else {
      setIsOpen(false);
      setEditor(null);
    }
  };

  const confirmCloseAction = () => {
    setIsOpen(false);
    setEditor(null);
    setConfirmClose(false);
  };

  if (!isAdmin) return null;

  if (!isOpen) {
    return (
      <div className="mb-4">
        <Button
          onClick={() => setIsOpen(true)}
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="open-visual-builder"
        >
          <Paintbrush className="w-4 h-4 mr-2" />
          Visual Page Builder (Drag & Drop)
        </Button>
      </div>
    );
  }

  return (
    <>
      <PremiumPopup
        isOpen={popup.isOpen}
        onClose={() => setPopup({ ...popup, isOpen: false })}
        type={popup.type}
        message={popup.message}
      />
      <PremiumPopup
        isOpen={confirmClose}
        onClose={() => setConfirmClose(false)}
        type="confirm"
        message="Έχεις μη αποθηκευμένες αλλαγές. Θέλεις να κλείσεις χωρίς αποθήκευση;"
        onConfirm={confirmCloseAction}
        confirmText="ΝΑΙ, ΚΛΕΙΣΙΜΟ"
        cancelText="ΑΚΥΡΩΣΗ"
      />
      <div className="fixed inset-0 z-50 bg-black">
        {/* Top Toolbar */}
      <div className="bg-gray-900 border-b border-white/10 p-3 flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <h3 className="text-xl font-bold text-white">
            🎨 Visual Page Builder - {pageId}
          </h3>
          <div className="panel__basic-actions flex space-x-1"></div>
        </div>
        <div className="flex space-x-2">
          <div className="text-sm text-gray-400 mr-4 flex items-center">
            💡 <span className="ml-1">Σύρε blocks, κλικ για edit, Delete για διαγραφή</span>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-green-600 hover:bg-green-700"
            data-testid="save-builder"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Αποθήκευση...' : 'Αποθήκευση'}
          </Button>
          <Button
            onClick={handleClose}
            variant="outline"
            className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
            data-testid="close-builder"
          >
            <X className="w-4 h-4 mr-2" />
            Κλείσιμο
          </Button>
        </div>
      </div>

      {/* Main Builder Area */}
      <div className="flex h-[calc(100vh-60px)]">
        {/* Left Sidebar - Blocks & Layers */}
        <div className="w-64 bg-gray-800 overflow-y-auto border-r border-white/10">
          <div className="p-4">
            <h4 className="text-white font-semibold mb-3">📦 Blocks</h4>
            <div id="blocks-container"></div>
          </div>
          <div className="p-4 border-t border-white/10">
            <h4 className="text-white font-semibold mb-3">📑 Layers</h4>
            <div id="layers-container"></div>
          </div>
        </div>

        {/* Center - Canvas */}
        <div className="flex-1 overflow-hidden bg-gray-900">
          <div ref={editorRef} className="h-full"></div>
        </div>

        {/* Right Sidebar - Styles */}
        <div className="w-64 bg-gray-800 overflow-y-auto border-l border-white/10">
          <div className="p-4">
            <h4 className="text-white font-semibold mb-3">🎨 Styles</h4>
            <div id="styles-container"></div>
          </div>
        </div>
      </div>

      {/* Bottom Help Bar */}
      <div className="bg-gray-900 border-t border-white/10 p-2 text-center text-sm text-gray-400">
        💡 <strong>Tip:</strong> Σύρε blocks από αριστερά, κάνε κλικ για edit, χρησιμοποίησε το style panel δεξιά για customization
      </div>
      </div>
    </>
  );
}

export default VisualPageBuilder;
